import { Icons } from '@commenda/kakashi';
import {
  getRelativeDateDisplay,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';

type ChatDateDividerVariant = 'default' | 'older' | 'new-message';

type ChatDateDividerProps = {
  date: string;
  variant?: ChatDateDividerVariant;
  onDateClick?: () => void;
};

function formatDateLabel(date: string): string {
  const relative = getRelativeDateDisplay(
    NonTimezoneSensitiveDate.fromNativeDate(new Date(date)).toISO8601String()
  );

  if (relative === 'Today' || relative === 'Tomorrow') return relative;

  // Check if yesterday
  const now = NonTimezoneSensitiveDate.now();
  const input = NonTimezoneSensitiveDate.fromNativeDate(new Date(date));
  if (now.add(-1, 'days').diff(input) === 0) return 'Yesterday';

  // Format as "Wednesday, Jan 21"
  const d = new Date(date);
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.getDate();
  return `${dayName}, ${month} ${day}`;
}

const Line = ({ variant }: { variant: ChatDateDividerVariant }) => {
  if (variant === 'new-message')
    return <div className="flex-1 h-px min-w-0 bg-text-error" />;

  if (variant === 'older')
    return (
      <div className="flex-1 h-px min-w-0 border-t border-dashed border-border-secondary" />
    );

  return <div className="flex-1 h-px min-w-0 bg-border-secondary" />;
};

const ChatDateDivider = ({
  date,
  variant = 'default',
  onDateClick,
}: ChatDateDividerProps) => {
  const label = formatDateLabel(date);
  const isNewMessage = variant === 'new-message';

  return (
    <div className="flex items-center w-full">
      <Line variant={variant} />
      <button
        type="button"
        onClick={onDateClick}
        className="flex items-center bg-bg-primary border border-border-secondary rounded-full px-xl py-sm cursor-pointer shrink-0"
      >
        <span className="font-mulish text-xs font-medium leading-[15px] text-text-secondary whitespace-nowrap">
          {label}
        </span>
      </button>
      {isNewMessage ? (
        <div className="flex-1 min-w-0 flex items-center gap-md">
          <div className="flex-1 h-px bg-text-error" />
          <span className="font-mulish text-xs font-medium leading-[15px] text-text-error whitespace-nowrap shrink-0">
            New
          </span>
        </div>
      ) : (
        <Line variant={variant} />
      )}
    </div>
  );
};

export default ChatDateDivider;
